/**
 * A React hook for accessing connected gamepads.
 * @qal
 */
import { useEffect, useRef, useState } from "react";

const haveEvents =
  typeof window !== "undefined" && "GamepadEvent" in window;
const haveWebkitEvents =
  typeof window !== "undefined" && "WebKitGamepadEvent" in window;

function getGamepads() {
  if (typeof navigator === "undefined") return [];
  if (navigator.getGamepads) return navigator.getGamepads();
  if (navigator.webkitGetGamepads) return navigator.webkitGetGamepads();
  return [];
}

// Normalize a raw Gamepad into a plain, serializable snapshot for state.
function snapshot(gp) {
  return {
    index: gp.index,
    id: gp.id,
    mapping: gp.mapping,
    buttons: Array.from(gp.buttons, (b) =>
      typeof b === "object"
        ? { pressed: b.pressed, touched: !!b.touched, value: b.value }
        : { pressed: b === 1.0, touched: false, value: b }
    ),
    axes: Array.from(gp.axes),
  };
}

// Polls the Gamepad API on every animation frame and returns the currently
// connected, standard-mapping gamepads as plain snapshots, sorted by index.
export function useGamepads() {
  // Map of index -> snapshot. Using an object keyed by index survives
  // reconnects cleanly.
  const [controllers, setControllers] = useState({});
  const rafRef = useRef(null);
  // Track which indices are "connected" so we keep polling them even if a
  // browser only exposes gamepads through polling (no events).
  const connected = useRef(new Set());

  useEffect(() => {
    const onConnect = (e) => {
      connected.current.add(e.gamepad.index);
    };
    const onDisconnect = (e) => {
      connected.current.delete(e.gamepad.index);
      setControllers((prev) => {
        const next = { ...prev };
        delete next[e.gamepad.index];
        return next;
      });
    };

    if (haveEvents) {
      window.addEventListener("gamepadconnected", onConnect);
      window.addEventListener("gamepaddisconnected", onDisconnect);
    } else if (haveWebkitEvents) {
      window.addEventListener("webkitgamepadconnected", onConnect);
      window.addEventListener("webkitgamepaddisconnected", onDisconnect);
    }

    const loop = () => {
      const pads = getGamepads();
      const filterPads = pads.filter((gp) => true); // filter out nulls
      const next = {};
      for (let i = 0; i < filterPads.length; i++) {
        const gp = filterPads[i];
        if (!gp) continue;
        // In event-less browsers, discover pads by presence; otherwise trust
        // the connected set but still accept anything the API reports.
        connected.current.add(gp.index);
        next[gp.index] = snapshot(gp);
      }
      setControllers(next);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (haveEvents) {
        window.removeEventListener("gamepadconnected", onConnect);
        window.removeEventListener("gamepaddisconnected", onDisconnect);
      } else if (haveWebkitEvents) {
        window.removeEventListener("webkitgamepadconnected", onConnect);
        window.removeEventListener("webkitgamepaddisconnected", onDisconnect);
      }
    };
  }, []);

  return Object.values(controllers).sort((a, b) => a.index - b.index);
}
